import { AdminSendVisualsToEditorPayload, useSendVisualsToEditor } from 'dataQueries'
import { AxiosError, AxiosResponse } from 'axios'
import { Dispatch, FC, ReactNode, SetStateAction, createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'

import { AssignmentDTOIsAdministratorDTO } from 'utils/typeguards'
import { AssignmentStage } from 'constants/assignment'
import AssignmentStageAdvice from 'components/common/AssignmentStageAdvice/AssignmentStageAdvice'
import { ESProduct } from 'constants/product'
import { Nullable } from 'models/helpers'
import { PIPEDRIVE_INFINITY } from 'constants/pipedrive'
import { StatusResponse } from 'models/redux'
import { UseMutationResult } from '@tanstack/react-query'
import { useActionPopup } from 'utils/hooks'
import { useAuth0 } from 'utils/auth'
import { useGalleryAssignment } from './GalleryAssignment.context'
import { useGalleryComments } from './GalleryComments.context'
import { useGalleryConstants } from './GalleryConstants.context'
import { useGalleryProduct } from './GalleryProduct.context'
import { useGalleryVisualSelection } from './GalleryVisualSelection.context'
import { useGalleryVisualsMeta } from './GalleryVisualsMeta.context'
import { useGetAdminEditorProducts } from 'dataQueries'
import moment from 'moment'
import { useTranslation } from 'react-i18next'

interface GalleryEditorProductInterface {
  editorProducts?: ESProduct[]
  editorProduct: ESProduct | 'NONE'
  setEditorProduct: Dispatch<SetStateAction<ESProduct | 'NONE'>>
  sendAssignmentVisualsToEditor?: Nullable<UseMutationResult<AxiosResponse<StatusResponse, any>, AxiosError<unknown, any>, AdminSendVisualsToEditorPayload, unknown>>
  canSendAssignmentVisualsToEditor: boolean
  assignmentVisualsAlreadySentToEditor: boolean
  cannotSendAssignmentVisualsToEditorInfo: string | JSX.Element
  canEditEditorProduct: boolean
  canEditEditorComments: boolean
  /** The line is at the editor stage with no open Esoft order and no send has succeeded yet */
  showNoOpenEsoftOrderNotice: boolean
  initiateSendAssignmentVisualsToEditor: () => Promise<void>
  /** Reference of the open Esoft order that locks the send */
  openEsoftOrderReference?: Nullable<string>
  /** Only the open Esoft order locks the send, so Quality may replace it */
  showSendAnyway: boolean
  canSendAnyway: boolean
  /** Why the offered Send anyway is disabled, empty otherwise */
  cannotSendAnywayInfo: string
  initiateSendAnyway: () => Promise<void>
}

const defaultGalleryEditorProductValue: GalleryEditorProductInterface = {
  editorProduct: 'NONE',
  setEditorProduct: () => { throw new Error('setEditorProduct is undefined') },
  canSendAssignmentVisualsToEditor: false,
  assignmentVisualsAlreadySentToEditor: false,
  cannotSendAssignmentVisualsToEditorInfo: '',
  canEditEditorProduct: false,
  canEditEditorComments: false,
  showNoOpenEsoftOrderNotice: false,
  initiateSendAssignmentVisualsToEditor: async () => { throw new Error('initiateSendAssignmentVisualsToEditor is undefined') },
  showSendAnyway: false,
  canSendAnyway: false,
  cannotSendAnywayInfo: '',
  initiateSendAnyway: async () => { throw new Error('initiateSendAnyway is undefined') },
}

/** Gallery editor product context */
export const GalleryEditorProductContext = createContext<GalleryEditorProductInterface>(defaultGalleryEditorProductValue)
/** Gallery editor product context hook */
export const useGalleryEditorProduct = (): GalleryEditorProductInterface => useContext(GalleryEditorProductContext)

/** PROD showed accidental double sends under 10 minutes apart and deliberate replacements of stuck orders later. */
const SEND_ANYWAY_MIN_ORDER_AGE_MINUTES = 10

/** setTimeout fires at once for a longer delay */
const MAX_TIMEOUT_MS = 2 ** 31 - 1

/** Context provider for gallery editor product -- ADMIN ONLY */
export const GalleryEditorProductContextProvider: FC<{
  assignmentId: string
  children?: ReactNode
}> = ({
  assignmentId,
  children,
}) => {
    const { roles } = useAuth0()
    const { t } = useTranslation(['gallery', 'deal_assignment'])
    const { showConfirm } = useActionPopup()

    const {
      NONE_EDITOR_PRODUCT,
      stagesUnlockingSendAssignmentVisualsToEditor,
      stagesWithVisualsAtEditor,
    } = useGalleryConstants()

    const {
      assignmentData,
      assignmentStage,
      assignment,
      isVisualSendToClient,
      isAssignmentCancelled,
    } = useGalleryAssignment()

    const {
      isVirtualVisit,
      isFloorPlan,
      product,
      isVirtualVisitMatterport,
      isAIEditingProduct,
    } = useGalleryProduct()

    const {
      prepurchasedVisualsTotal,
    } = useGalleryVisualsMeta()

    const {
      selected,
    } = useGalleryVisualSelection()

    const {
      adminCommentsForEditor,
    } = useGalleryComments()

    const sendAssignmentVisualsToEditor = useSendVisualsToEditor()

    const assignmentIdLoaded = useRef<string | null>(null)

    // Fetch editor products for admin
    const editorProductsResponse = useGetAdminEditorProducts()
    const editorProducts = useMemo(() => editorProductsResponse.data, [editorProductsResponse])

    const [editorProduct, setEditorProduct] = useState<ESProduct | typeof NONE_EDITOR_PRODUCT>(NONE_EDITOR_PRODUCT)

    // Undefined (a backend that does not send it) keeps the stage-only rules.
    const { openEsoftOrder, deliveredEsoftOrder, openEsoftOrderReference, openEsoftOrderPlacedDate, openEsoftOrderTransferRunning } = useMemo(() => {
      if (!assignmentData || !AssignmentDTOIsAdministratorDTO(assignmentData)) return { openEsoftOrder: undefined, deliveredEsoftOrder: undefined, openEsoftOrderReference: undefined, openEsoftOrderPlacedDate: undefined, openEsoftOrderTransferRunning: undefined }
      return {
        openEsoftOrder: assignmentData.hasOpenEsoftOrder,
        deliveredEsoftOrder: assignmentData.hasDeliveredEsoftOrder,
        openEsoftOrderReference: assignmentData.openEsoftOrderReference,
        openEsoftOrderPlacedDate: assignmentData.openEsoftOrderPlacedDate,
        openEsoftOrderTransferRunning: assignmentData.openEsoftOrderTransferRunning,
      }
    }, [assignmentData])

    /** Epoch ms from which the open order may be replaced; null without a placed date, which leaves the backend to refuse. */
    const sendAnywayAvailableAt = useMemo(() => {
      if (!openEsoftOrderPlacedDate) return null
      const placedDate = moment(openEsoftOrderPlacedDate, moment.ISO_8601)
      return placedDate.isValid() ? placedDate.add(SEND_ANYWAY_MIN_ORDER_AGE_MINUTES, 'minutes').valueOf() : null
    }, [openEsoftOrderPlacedDate])

    // Bumped by the timer below so the age check re-runs once the order is old enough.
    const [ageCheckTick, setAgeCheckTick] = useState(0)
    const isOpenOrderTooRecent = sendAnywayAvailableAt !== null && Date.now() < sendAnywayAvailableAt

    useEffect(() => {
      if (!isOpenOrderTooRecent || sendAnywayAvailableAt === null) return
      const timer = setTimeout(() => setAgeCheckTick(tick => tick + 1), Math.min(sendAnywayAvailableAt - Date.now(), MAX_TIMEOUT_MS))
      return () => clearTimeout(timer)
    }, [ageCheckTick, isOpenOrderTooRecent, sendAnywayAvailableAt])

    /**
     * The editor stage was set without an order at Esoft, so the visuals still have to be sent.
     * AI-edited lines sit at this stage while the AI edits them, so they stay locked.
     */
    const isAtEditorWithoutOpenEsoftOrder = useMemo(
      () => assignmentStage === AssignmentStage.VISUALS_SENT_BY_BKBN_TO_EDITOR && openEsoftOrder === false && !isAssignmentCancelled && !isAIEditingProduct,
      [assignmentStage, isAIEditingProduct, isAssignmentCancelled, openEsoftOrder]
    )

    /**
     * A round started from a delivered assignment reworks part of what was sent, not the whole purchase.
     * Delivered means the client stage, or the editor stage after an Esoft delivery (pulled back, or a stalled
     * rework order being replaced).
     * The terminal stage alone does not mean delivered — the cancel cascade parks assignments there too, and
     * the platform refuses to move those.
     */
    const isReworkRound = useMemo(
      () => (
        isVisualSendToClient
        || (assignmentStage === AssignmentStage.VISUALS_SENT_BY_BKBN_TO_EDITOR && deliveredEsoftOrder === true && !isAIEditingProduct)
      ) && !isAssignmentCancelled,
      [assignmentStage, deliveredEsoftOrder, isAIEditingProduct, isAssignmentCancelled, isVisualSendToClient]
    )

    /** Esoft already has an order for the line, possibly placed directly there, and the platform refuses a second one. */
    const isSendLockedByOpenEsoftOrder = useMemo(
      () => !!assignmentStage && stagesUnlockingSendAssignmentVisualsToEditor.has(assignmentStage) && openEsoftOrder === true,
      [assignmentStage, openEsoftOrder, stagesUnlockingSendAssignmentVisualsToEditor]
    )

    /**
     * Esoft can confirm an order and never deliver it; Quality then cancels it at Esoft and sends a replacement.
     * Without the order's reference (older backend) the lock stays.
     */
    const showSendAnyway = useMemo(
      () => !!openEsoftOrderReference && !isAssignmentCancelled && !isAIEditingProduct
        && (isSendLockedByOpenEsoftOrder || (assignmentStage === AssignmentStage.VISUALS_SENT_BY_BKBN_TO_EDITOR && openEsoftOrder === true)),
      [assignmentStage, isAIEditingProduct, isAssignmentCancelled, isSendLockedByOpenEsoftOrder, openEsoftOrder, openEsoftOrderReference]
    )

    // The platform refuses to move a cancelled line back to the editor.
    const stageUnlocksSend = useMemo(
      () => ((!!assignmentStage && stagesUnlockingSendAssignmentVisualsToEditor.has(assignmentStage) && !isSendLockedByOpenEsoftOrder) || isAtEditorWithoutOpenEsoftOrder) && !isAssignmentCancelled,
      [assignmentStage, isAssignmentCancelled, isAtEditorWithoutOpenEsoftOrder, isSendLockedByOpenEsoftOrder, stagesUnlockingSendAssignmentVisualsToEditor]
    )

    // The refetch after a send reports the new order; until it lands the mutation state hides the notice.
    const showNoOpenEsoftOrderNotice = isAtEditorWithoutOpenEsoftOrder && !sendAssignmentVisualsToEditor.isSuccess

    /** Product and selection rules of a send, whatever unlocks it */
    const selectionAllowsSend = useMemo(() => {
      // A refused send must leave the control usable: the reason is on the status card and the fix is a new selection.
      if (!sendAssignmentVisualsToEditor?.isIdle && !sendAssignmentVisualsToEditor?.isError) return false
      if (editorProduct === NONE_EDITOR_PRODUCT) return false

      if (!isReworkRound && (isVirtualVisit || isFloorPlan)) return true

      if (isReworkRound || prepurchasedVisualsTotal === PIPEDRIVE_INFINITY) return selected.size > 0
      return selected.size >= prepurchasedVisualsTotal
    }, [NONE_EDITOR_PRODUCT, editorProduct, isFloorPlan, isReworkRound, isVirtualVisit, prepurchasedVisualsTotal, selected.size, sendAssignmentVisualsToEditor])

    const canSendAssignmentVisualsToEditor = !!assignmentStage && stageUnlocksSend && selectionAllowsSend

    // The backend refuses a replacement while the open order's transfer runs; undefined (older backend) does not lock.
    const isOpenOrderTransferRunning = openEsoftOrderTransferRunning === true

    const canSendAnyway = showSendAnyway && selectionAllowsSend && !isOpenOrderTooRecent && !isOpenOrderTransferRunning

    const assignmentVisualsAlreadySentToEditor = useMemo(
      () => (!!assignmentStage && stagesWithVisualsAtEditor.has(assignmentStage) && !isAtEditorWithoutOpenEsoftOrder) || isSendLockedByOpenEsoftOrder,
      [assignmentStage, isAtEditorWithoutOpenEsoftOrder, isSendLockedByOpenEsoftOrder, stagesWithVisualsAtEditor]
    )
    const cannotSendAssignmentVisualsToEditorInfo = useMemo(() => {
      if ((!(!sendAssignmentVisualsToEditor.data || sendAssignmentVisualsToEditor.isIdle)) || assignmentVisualsAlreadySentToEditor) {
        return t('sent_to_editor')
      } else if (isAssignmentCancelled) {
        return t('deal_assignment:cancelled')
      } else if (!assignmentStage || !stageUnlocksSend) {
        return <AssignmentStageAdvice id="send_to_editor" currentStage={assignmentStage} validStages={stagesUnlockingSendAssignmentVisualsToEditor} />
      } else if (editorProduct === NONE_EDITOR_PRODUCT) {
        return t('select_editor_product')
      } else if (!(isVirtualVisit && isFloorPlan) && !(selected.size >= prepurchasedVisualsTotal || ((isReworkRound || prepurchasedVisualsTotal === PIPEDRIVE_INFINITY) && selected.size > 0))) {
        return t('select_more_visuals')
      } else {
        return ''
      }
    }, [NONE_EDITOR_PRODUCT, assignmentStage, assignmentVisualsAlreadySentToEditor, editorProduct, isAssignmentCancelled, isFloorPlan, isReworkRound, isVirtualVisit, prepurchasedVisualsTotal, selected.size, sendAssignmentVisualsToEditor, stageUnlocksSend, stagesUnlockingSendAssignmentVisualsToEditor, t])

    const cannotSendAnywayInfo = useMemo(() => {
      if (!showSendAnyway || canSendAnyway) return ''
      // A send in flight or placed: the status card reports it, and the page allows no replacement until a reload.
      if (!sendAssignmentVisualsToEditor.isIdle && !sendAssignmentVisualsToEditor.isError) return ''
      if (isOpenOrderTransferRunning) return t('send_anyway_transfer_running')
      if (isOpenOrderTooRecent && sendAnywayAvailableAt !== null) return t('send_anyway_available_at', { time: moment(sendAnywayAvailableAt).format('HH:mm') })
      if (editorProduct === NONE_EDITOR_PRODUCT) return t('select_editor_product')
      return t('select_more_visuals')
    }, [NONE_EDITOR_PRODUCT, canSendAnyway, editorProduct, isOpenOrderTooRecent, isOpenOrderTransferRunning, sendAnywayAvailableAt, sendAssignmentVisualsToEditor, showSendAnyway, t])

    const isSendIdleOrRefused = !sendAssignmentVisualsToEditor || sendAssignmentVisualsToEditor.isIdle || sendAssignmentVisualsToEditor.isError

    const canEditEditorComments = !!assignmentStage && stageUnlocksSend && isSendIdleOrRefused

    // A replacement may need another product, e.g. when the open order was placed on Esoft directly; the comment stays as sent.
    const canEditEditorProduct = !!assignmentStage && (stageUnlocksSend || showSendAnyway) && isSendIdleOrRefused

    const confirmAndSend = useCallback(async (prompt: string, replaceOpenOrderReference?: string) => {
      if (editorProduct === NONE_EDITOR_PRODUCT) return
      if (!(await showConfirm(prompt))) return
      sendAssignmentVisualsToEditor.mutate({
        assignmentId,
        filenames: Array.from(selected),
        product: editorProduct,
        comment: adminCommentsForEditor,
        ...(replaceOpenOrderReference ? { replaceOpenOrderReference } : {}),
      })
    }, [NONE_EDITOR_PRODUCT, adminCommentsForEditor, assignmentId, editorProduct, selected, sendAssignmentVisualsToEditor, showConfirm])

    const initiateSendAssignmentVisualsToEditor = useCallback(async () => {
      if (!roles.isAdmin) return
      if (!canSendAssignmentVisualsToEditor) return
      const prompt = isReworkRound
        ? t('send_to_editor_rework_prompt', { count: selected.size })
        : t('send_to_editor_prompt')
      await confirmAndSend(prompt)
    }, [canSendAssignmentVisualsToEditor, confirmAndSend, isReworkRound, roles.isAdmin, selected.size, t])

    const initiateSendAnyway = useCallback(async () => {
      if (!roles.isAdmin) return
      if (!canSendAnyway || !openEsoftOrderReference) return
      // The send names the order the prompt showed, so the backend refuses it if another one is open by then.
      await confirmAndSend(t('send_anyway_prompt', { reference: openEsoftOrderReference }), openEsoftOrderReference)
    }, [canSendAnyway, confirmAndSend, openEsoftOrderReference, roles.isAdmin, t])

    // Fill in editor product upon assignment received
    useEffect(() => {
      if (!assignment.isSuccess) return
      if (!assignmentData) return
      // Checks if it's first render or if the assignment changed
      if (!!assignmentIdLoaded.current && assignmentData.id === assignmentIdLoaded.current) return

      assignmentIdLoaded.current = assignmentData.id

      if (AssignmentDTOIsAdministratorDTO(assignmentData)) {
        if (assignmentData.editorProducts.length > 0 && ESProduct[assignmentData.editorProducts[0] as ESProduct]) {
          setEditorProduct(assignmentData.editorProducts[0])
        } else if (isFloorPlan) {
          // Prefill editor product for floor plan assignment
          setEditorProduct(ESProduct.FLOORPLAN_2D_ADVANCED_MATTERPORT_100)
        }
        // Validates product is virtual visit, real state category and kind HD
        if (isVirtualVisitMatterport) {
          // Prefill editor product to send matterport links directly to the editor
          setEditorProduct(ESProduct.QC_MATTERPORT)
        }
      }

    }, [isFloorPlan, assignmentData, assignment, product?.category, product, isVirtualVisitMatterport])

    useEffect(() => {
      sendAssignmentVisualsToEditor.reset()

      // Omit mutation to prevent looping
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [assignmentId])

    return (
      <GalleryEditorProductContext.Provider
        value={{
          editorProducts,
          editorProduct,
          setEditorProduct,
          sendAssignmentVisualsToEditor,
          canSendAssignmentVisualsToEditor,
          assignmentVisualsAlreadySentToEditor,
          cannotSendAssignmentVisualsToEditorInfo,
          canEditEditorProduct,
          canEditEditorComments,
          showNoOpenEsoftOrderNotice,
          initiateSendAssignmentVisualsToEditor,
          openEsoftOrderReference,
          showSendAnyway,
          canSendAnyway,
          cannotSendAnywayInfo,
          initiateSendAnyway,
        }}
      >
        {children}
      </GalleryEditorProductContext.Provider>
    )
  }
